import { create } from "zustand";
import { UpdateFn, Updater } from "../../types";
import { Item, ItemWidget, Widget } from "../../types/widget";
import { WIDGETS } from "../../constants/widgets";

interface State {
  items: Item[];
  baseId: number;
}

interface Action {
  setter: <T extends keyof State>(key: T, updater: Updater<State[T]>) => void;

  setItems: UpdateFn<Item[]>;
  addItem: (widgetNames: string[]) => Item;
  rmItem: (id: number) => void;
  getItem: (id: number) => Item | null;
  getItemIdx: (id: number) => number;
  mvUpItem: (id: number) => void;
  mvDownItem: (id: number) => void;
  clearItemWidgets: (id: number) => void;
  itemHasOneMan: (id: number) => boolean;

  getWidgetByName: (widgetName: string) => Widget | null;
  createItemWidget: (widgetName: string, flex?: number) => ItemWidget | null;
  setItemWidgetFlex: (itemId: number, widgetId: number, flex: number) => void;
  addWidgetToItem: (widgetName: string, itemId: number, flex?: number) => void;
  rmWidgetFromItem: (widgetName: string, itemId: number) => void;
  incWidgetFlex: (itemId: number, widgetId: number) => void;
  decWidgetFlex: (itemId: number, widgetId: number) => void;
  setItemWidgetToLeft: (itemId: number, widgetId: number) => void;
  setItemWidgetToRight: (itemId: number, widgetId: number) => void;

  getId: () => number;
}

const useWidgetStore = create<State & Action>((set, get) => ({
  items: [],
  baseId: 10,

  setter: <T extends keyof State>(key: T, updater: Updater<State[T]>): void => {
    if (typeof updater === "function") {
      set((state) => ({ [key]: updater(state[key]) }));
    } else {
      set({ [key]: updater });
    }
  },

  setItems: (items) => {
    get().setter("items", items);
  },

  addItem: (widgetNames) => {
    let itemWidgets: ItemWidget[] = [];

    widgetNames.forEach((wn) => {
      let widget = get().createItemWidget(wn);
      if (widget) itemWidgets.push(widget);
    });

    let item: Item = { widgets: itemWidgets, id: get().getId() };

    get().setItems((prev) => [...prev, item]);

    return item;
  },

  rmItem: (id) => {
    get().setItems((prev) => {
      return prev.filter((p) => p.id !== id);
    });
  },

  getItem: (id) => {
    let res = get().items.filter((p) => p.id === id);
    return res.length ? res[0] : null;
  },

  getItemIdx: (id) => {
    let res = -1;
    get().items.forEach((item, idx) => {
      if (item.id === id) res = idx;
    });
    return res;
  },

  mvUpItem: (id) => {
    get().setItems((prev) => {
      for (let i = 0; i < prev.length; i++) {
        if (prev[i].id !== id) continue;

        if (i === 0) break;

        let tmp = prev[i];
        prev[i] = prev[i - 1];
        prev[i - 1] = tmp;
        break;
      }
      return [...prev];
    });
  },

  mvDownItem: (id) => {
    get().setItems((prev) => {
      for (let i = 0; i < prev.length; i++) {
        if (prev[i].id !== id) continue;

        if (i === prev.length - 1) break;

        let tmp = prev[i];
        prev[i] = prev[i + 1];
        prev[i + 1] = tmp;
        break;
      }
      return [...prev];
    });
  },

  clearItemWidgets: (id) => {
    get().setItems((prev) => {
      let idx = get().getItemIdx(id);
      if (idx < 0) return [...prev];

      prev[idx].widgets = [];

      return [...prev];
    });
  },

  itemHasOneMan: (id) => {
    let item = get().getItem(id);
    if (!item) return false;

    for (let i = 0; i < item.widgets.length; i++) {
      if (item.widgets[i].oneMan) return true;
    }

    return false;
  },

  getWidgetByName: (widgetName) => {
    let res = WIDGETS.filter((w) => w.name === widgetName);
    return res.length ? res[0] : null;
  },

  createItemWidget: (widgetName, flex) => {
    let widget = get().getWidgetByName(widgetName);
    if (!widget) return null;

    return { ...widget, flex: flex ?? widget.prefFlex ?? 1, id: get().getId() };
  },

  setItemWidgetFlex: (itemId, widgetId, flex) => {
    get().setItems((prev) => {
      let idx = get().getItemIdx(itemId);
      if (idx < 0) return [...prev];

      let item = prev[idx];
      for (let i = 0; i < item.widgets.length; i++) {
        if (item.widgets[i].id !== widgetId) continue;
        item.widgets[i].flex = flex;
        break;
      }
      prev[idx] = item;
      return [...prev];
    });
  },

  addWidgetToItem: (widgetName, itemId, flex) => {
    get().setItems((prev) => {
      let idx = get().getItemIdx(itemId);
      if (idx < 0) return [...prev];

      let item = prev[idx];
      let widget = get().createItemWidget(widgetName, flex);
      if (!widget) return [...prev];

      item.widgets.push(widget);

      prev[idx] = item;
      return [...prev];
    });
  },

  rmWidgetFromItem: (widgetName, itemId) => {
    get().setItems((prev) => {
      let idx = get().getItemIdx(itemId);
      if (idx < 0) return [...prev];

      let item = prev[idx];
      item.widgets = item.widgets.filter((w) => w.name !== widgetName);

      prev[idx] = item;
      return [...prev];
    });
  },

  incWidgetFlex: (itemId, widgetId) => {
    get().setItems((prev) => {
      let idx = get().getItemIdx(itemId);
      if (idx < 0) return [...prev];

      let item = prev[idx];
      for (let i = 0; i < item.widgets.length; i++) {
        if (item.widgets[i].id !== widgetId) continue;
        item.widgets[i].flex++;
      }

      prev[idx] = item;
      return [...prev];
    });
  },

  decWidgetFlex: (itemId, widgetId) => {
    get().setItems((prev) => {
      let idx = get().getItemIdx(itemId);
      if (idx < 0) return [...prev];

      let item = prev[idx];
      for (let i = 0; i < item.widgets.length; i++) {
        if (item.widgets[i].id !== widgetId) continue;
        if (item.widgets[i].flex !== 1) item.widgets[i].flex--;
      }

      prev[idx] = item;
      return [...prev];
    });
  },

  setItemWidgetToLeft: (itemId, widgetId) => {
    get().setItems((prev) => {
      let idx = get().getItemIdx(itemId);
      if (idx < 0) return [...prev];

      let item = prev[idx];
      for (let i = 0; i < item.widgets.length; i++) {
        if (item.widgets[i].id !== widgetId) continue;
        if (i === item.widgets.length - 1) break;

        let tmp = item.widgets[i];
        item.widgets[i] = item.widgets[i + 1];
        item.widgets[i + 1] = tmp;
        break;
      }

      prev[idx] = item;
      return [...prev];
    });
  },

  setItemWidgetToRight: (itemId, widgetId) => {
    get().setItems((prev) => {
      let idx = get().getItemIdx(itemId);
      if (idx < 0) return [...prev];

      let item = prev[idx];
      for (let i = 0; i < item.widgets.length; i++) {
        if (item.widgets[i].id !== widgetId) continue;
        if (i === 0) break;

        let tmp = item.widgets[i];
        item.widgets[i] = item.widgets[i - 1];
        item.widgets[i - 1] = tmp;
        break;
      }

      prev[idx] = item;
      return [...prev];
    });
  },

  getId: () => {
    let res = get().baseId;
    set({ baseId: res + 1 });
    return res;
  },
}));

export default useWidgetStore;
